import type { TrainSceneState } from './train-scene';

export type CharacterId = 'mara' | 'wizard' | 'robot' | 'chuck';
export type SessionCreated = {
  sessionId: string;
  greetingTurnId: string;
  greeting: string;
  characterId: CharacterId;
};
export type TrainSpeedAction = {
  type: 'set_train_speed';
  targetKph: number;
  transitionSeconds: number;
};
export type TurnCreated = {
  turnId: string;
  transcript: string;
  reply: string;
  action: TrainSpeedAction | null;
};
export type Health = {
  ready: boolean;
  groqConfigured: boolean;
  stt: boolean;
  sttProvider: 'remote' | 'groq';
  higgs: boolean;
  ditto: boolean;
  origin: string;
};
export type VideoQueueStatus =
  | { state: 'not-requested'; ahead: 0 }
  | { state: 'queued'; ahead: number }
  | { state: 'rendering'; ahead: 0 };

export class ApiError extends Error {
  constructor(message: string, public readonly status: number) {
    super(message);
  }
}

async function expectJson<T>(response: Response): Promise<T> {
  if (response.ok) return response.json() as Promise<T>;
  let message = 'The portrait could not complete that request.';
  try {
    const body = (await response.json()) as { error?: unknown };
    if (typeof body.error === 'string') message = body.error;
  } catch {
    // Keep the safe generic message for non-JSON failures.
  }
  throw new ApiError(message, response.status);
}

export async function createSession(
  characterId: CharacterId = 'mara',
  signal?: AbortSignal,
): Promise<SessionCreated> {
  const response = await fetch(`/api/sessions?character=${encodeURIComponent(characterId)}`, {
    method: 'POST',
    signal,
  });
  return expectJson<SessionCreated>(response);
}

export async function submitTurn(
  sessionId: string,
  wav: Blob,
  signal?: AbortSignal,
  sceneState?: TrainSceneState,
): Promise<TurnCreated> {
  const form = new FormData();
  form.set('audio', wav, 'utterance.wav');
  if (sceneState) form.set('sceneState', JSON.stringify(sceneState));
  const response = await fetch(`/api/sessions/${encodeURIComponent(sessionId)}/turns`, {
    method: 'POST',
    body: form,
    signal,
  });
  return expectJson<TurnCreated>(response);
}

export async function submitSceneEvent(
  sessionId: string,
  announcement: string,
  sceneState: TrainSceneState,
  signal?: AbortSignal,
): Promise<TurnCreated> {
  const response = await fetch(`/api/sessions/${encodeURIComponent(sessionId)}/events`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ announcement, sceneState }),
    signal,
  });
  return expectJson<TurnCreated>(response);
}

export async function deleteSession(sessionId: string): Promise<void> {
  await fetch(`/api/sessions/${encodeURIComponent(sessionId)}`, {
    method: 'DELETE',
    keepalive: true,
  }).catch(() => undefined);
}

export function turnAudioUrl(sessionId: string, turnId: string): string {
  return `/api/sessions/${encodeURIComponent(sessionId)}/turns/${encodeURIComponent(turnId)}/audio`;
}

export function turnVideoUrl(sessionId: string, turnId: string): string {
  return `/api/sessions/${encodeURIComponent(sessionId)}/turns/${encodeURIComponent(turnId)}/video`;
}

export async function fetchVideoQueueStatus(
  sessionId: string,
  turnId: string,
  signal?: AbortSignal,
): Promise<VideoQueueStatus> {
  const response = await fetch(
    `${turnVideoUrl(sessionId, turnId)}/status`,
    { signal },
  );
  return expectJson<VideoQueueStatus>(response);
}

export async function fetchHealth(signal?: AbortSignal): Promise<Health> {
  const response = await fetch('/api/health', { signal });
  return expectJson<Health>(response);
}
